import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";
import type { AttributeField, PropertyAttributeRow } from "@/features/properties/types/propertyAttributes";
import { resolveSurveyAttributes } from "@/features/site-survey-editor/services/resolveSurveyAttributes";
import { selectWithSurveySource } from "@/shared/integrations/supabase/surveySource";

const db = supabase;

function parseTree<T>(v: unknown): T {
  return (typeof v === "string" ? JSON.parse(v) : v) as T;
}

/**
 * Fetch the latest survey for a property and resolve which source to render:
 * the self-describing `prod_data` (when `survey_source === 'prod_data'`) or the
 * legacy `data` tree. Shared by every survey reader so the switch is applied
 * consistently. See resolveSurveyAttributes for the gate.
 */
export async function fetchResolvedSurveyAttributes(propertyId: string): Promise<AttributeField[]> {
  const { data: rows, error } = await db
    .from("property_attributes")
    .select(await selectWithSurveySource("id, property_id, captured_at, data, prod_data, normalization_status, processing_events, last_attempt_at, attempt_count, ref_id"))
    .eq("property_id", propertyId)
    .order("captured_at", { ascending: false })
    .limit(1);
  if (error) throw error;

  const latest = (Array.isArray(rows) && rows.length > 0 ? rows[0] : null) as
    | (PropertyAttributeRow & { prod_data?: unknown; survey_source?: string | null })
    | null;
  if (!latest) return [];

  const data = parseTree<AttributeField[]>(latest.data ?? []) ?? [];
  const prodData = (latest.prod_data ?? null) as Record<string, unknown> | null;
  return resolveSurveyAttributes({ surveySource: latest.survey_source, prodData, data }).fields;
}

/** Survey attributes for a property (self-describing prod_data when flagged, else data). */
export function usePropertyAttributes(propertyId: string | undefined) {
  return useQuery({
    queryKey: ["property-attributes", propertyId],
    queryFn: () => fetchResolvedSurveyAttributes(propertyId!),
    enabled: !!propertyId,
  });
}
